import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { printService } from 'api/services/printService';
import type { PairingCode } from 'api/types';
import { fmtTime, apiMessage } from 'components/modules/print-module/printUi';

/**
 * Pairing codes not yet used and not expired. Only a hint of each code is
 * kept; a code that leaked can be withdrawn here. A small list, read whole.
 */
interface Props {
  isOpen: boolean;
  canWithdraw: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const PairingCodesModal: React.FC<Props> = ({ isOpen, canWithdraw, onClose, onChanged }) => {
  const [codes, setCodes] = useState<PairingCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<PairingCode | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setCodes(await printService.getPairingCodes()); }
    catch (err) { setError(apiMessage(err, 'The pairing codes could not be loaded.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (isOpen) void load(); }, [isOpen, load]);

  const withdraw = async () => {
    if (!withdrawing) return;
    setBusy(true);
    try {
      await printService.withdrawPairingCode(withdrawing.id);
      notificationService.toast.success(`The code for "${withdrawing.agentName}" was withdrawn.`);
      setWithdrawing(null);
      void load(); onChanged();
    } catch (err) {
      notificationService.toast.error(apiMessage(err, 'The code could not be withdrawn.'));
    } finally { setBusy(false); }
  };

  if (!isOpen) return null;
  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-md" role="dialog" aria-modal="true" style={{ minHeight: 'auto', maxWidth: 720 }} data-pairing-codes>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Open pairing codes</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close"><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          {error && <div className="form-error-message">{error}</div>}
          {loading && <p style={{ margin: 0 }}>Loading…</p>}
          {!loading && !error && codes.length === 0 && <p style={{ margin: 0, color: 'var(--text-muted)' }}>No open pairing codes.</p>}
          {!loading && codes.length > 0 && (
            <table className="prn-events">
              <thead><tr><th>Code</th><th>For</th><th>Purpose</th><th>Expires</th><th>Created by</th><th /></tr></thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} data-pairing-row={c.agentName}>
                    <td style={{ fontFamily: 'monospace' }}>{c.codeHint}</td>
                    <td>{c.agentName}{c.siteName ? ` · ${c.siteName}` : ''}{c.requireApproval ? <span className="prn-sub">needs approval</span> : null}</td>
                    <td>{c.purpose === 'REPLACE_AGENT' ? `Replace ${c.replaceAgentName ?? 'agent'}` : 'New agent'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(c.expiresAt)}</td>
                    <td>{c.createdByName ?? '—'}</td>
                    <td>{canWithdraw && <button type="button" className="btn btn-secondary" style={{ padding: '2px 10px' }} onClick={() => setWithdrawing(c)}>Withdraw</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={() => void load()} disabled={loading}>Refresh</button>
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
      <ConfirmDialog open={!!withdrawing} title="Withdraw the code?"
        message={`The code for "${withdrawing?.agentName ?? ''}" stops working now. An agent already paired with it is not affected.`}
        confirmLabel="Withdraw" cancelLabel="Keep it" variant="danger" loading={busy} loadingMessage="Withdrawing..."
        onConfirm={() => void withdraw()} onCancel={() => setWithdrawing(null)} />
    </>
  );
};

export default PairingCodesModal;
